// src/components/analytics/RxMixPanel.tsx
import React from "react";
import {
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  alpha,
  Box,
  TableContainer,
  Paper,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import numeral from "numeral";
import { TopLevelPaper } from "../../../../../../../themes/StyledComponents";

export interface RxSummaryRow {
  rxType: string;
  count: number;
  percentage: number;
  totalAmount: number;
  averageAmount: number;
}

interface Props {
  rows: RxSummaryRow[];
}

const palette = [
  "#456882",
  "#1B3C53",
  "#0c63aa",
  "#007ba0",
  "#fff",
  "#9DB1C9",
  "#6F879F",
];

const euro = (n: number) => numeral(n).format("0,0.00 $");

const RxMixPanel: React.FC<Props> = ({ rows }) => {
  return (
    <TopLevelPaper
      elevation={1}
      sx={{
        p: 2,
        borderRadius: 2,
        border: "0px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Typography variant="subtitle1" fontWeight={450} color="text.primary">
        Verteilung nach Rezept-Typ
      </Typography>

      {/* Donut */}
      <Box sx={{ height: 220 }}>
        <ResponsiveContainer>
          <PieChart margin={{ top: 10, right: 0, left: 0, bottom: 10 }}>
            <Pie
              data={rows}
              dataKey="percentage"
              nameKey="rxType"
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={70}
              paddingAngle={1}
              label={({ percentage }) => `${percentage.toFixed(0)} %`}
              labelLine={false}
            >
              {rows.map((_, i) => (
                <Cell key={`c-${i}`} fill={palette[i % palette.length]} />
              ))}
            </Pie>
            <ReTooltip
              formatter={(value: number, name: string) => [
                `${value.toFixed(1)} %`,
                name,
              ]}
            />
            <Legend
              layout="vertical"
              align="right"
              verticalAlign="middle"
              iconSize={10}
            />
          </PieChart>
        </ResponsiveContainer>
      </Box>

      {/* Mini table */}
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: 240,
          borderRadius: 1,
          overflowY: "auto",
          /* slim coloured scrollbar */
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": {
            bgcolor: alpha("#F9F3EF", 0.6),
          },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "#456882",
            borderRadius: 3,
          },
        }}
      >
        <Table
          sx={{
            "& .MuiTableCell-stickyHeader": {
              bgcolor: "#1B3C53",
              color: "#fff",
              backgroundClip: "padding-box",
              zIndex: 1,
            },
          }}
          stickyHeader
        >
          <TableHead sx={{ bgcolor: alpha("#1B3C53", 0.7) }}>
            <TableRow
              sx={{
                height: 48,
                "& td": { padding: 1 },
              }}
            >
              <TableCell sx={{ color: "#fff" }}>Typ</TableCell>
              <TableCell align="right" sx={{ color: "#fff" }}>
                Anzahl
              </TableCell>
              <TableCell align="right" sx={{ color: "#fff" }}>
                Gesamt (€)
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow
                key={r.rxType}
                sx={{
                  height: 36,
                  "& td": { padding: 1 },
                }}
              >
                <TableCell sx={{ fontSize: "1rem" }}>{r.rxType}</TableCell>
                <TableCell sx={{ fontSize: "1rem" }} align="right">
                  {r.count}
                </TableCell>
                <TableCell sx={{ fontSize: "1rem" }} align="right">
                  {euro(r.totalAmount)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </TopLevelPaper>
  );
};

export default RxMixPanel;
